import ConfirmNavigation from "@/components/navigation/ConfirmNavigation";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { loadCmsGoogleFonts } from "@/lib/cms/cms-google-fonts";
import {
  openPublicSiteInDraftPreview,
  openPublicSiteWithBypass,
} from "@/lib/cms/open-public-site-with-bypass";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  CreditCard,
  Eye,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  LayoutDashboard,
  LayoutTemplate,
  ListTree,
  Newspaper,
  Palette,
  Route,
  Scissors,
  Search,
  Settings,
  ShieldCheck,
  Store,
  Users,
  Wrench,
} from "lucide-react";
import React, { useEffect, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * CmsAdminShell — coquille « WordPress-like » du CMS avancé (Puck) : barre
 * latérale SOMBRE persistante (Pages / Menus / Apparence / Réglages) + zone de
 * contenu claire avec barre de titre et emplacement d'actions (Enregistrer,
 * Nouvelle page…). Toutes les routes `/cms/advanced/*` s'y insèrent, SAUF
 * l'éditeur Puck (`/cms/advanced/:id`, plein écran).
 *
 * `formIds` (pages formulaire Apparence/Réglages) : suivi « modifications non
 * enregistrées » (input/reset/submit sur les <form id=…>, même mécanique que
 * CMSLayout) + garde de navigation `ConfirmNavigation`.
 *
 * Les écrans Apparence/Réglages RÉUTILISENT les éditeurs de l'ancien CMS
 * (AppearanceEditor / GeneralSettingsEditor) — une seule source de vérité, les
 * anciennes routes `/cms/apparence` et `/cms/general-settings` restent en place.
 */

type NavItem = {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  /** Correspondance exacte (la racine matcherait sinon toutes les sections). */
  exact?: boolean;
  /** Entrée masquée si l'instance n'a pas la boutique activée. */
  requiresEshop?: boolean;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

/** Structure de navigation — libellés résolus à chaque render (pas au chargement du module). */
function buildNavGroups(cmsNew: ReturnType<typeof useCmsNew>): NavGroup[] {
  return [
    {
      title: cmsNew("cms.new.auto.title.vue_densemble", "Vue d'ensemble"),
      items: [
        {
          path: "/cms/advanced/accueil",
          label: cmsNew(
            "cms.new.auto.label.tableau_de_bord",
            "Tableau de bord",
          ),
          icon: LayoutDashboard,
        },
        {
          path: "/cms/advanced",
          label: cmsNew("cms.new.auto.label.pages", "Pages"),
          icon: FileText,
          exact: true,
        },
      ],
    },
    {
      title: cmsNew("cms.new.templates.navGroup", "Modèles"),
      items: [
        {
          path: "/cms/advanced/modeles",
          label: cmsNew(
            "cms.new.templates.navItem",
            "Bibliothèque de modèles",
          ),
          icon: LayoutTemplate,
        },
      ],
    },
    {
      title: cmsNew("cms.new.auto.title.parcours", "Parcours"),
      items: [
        {
          path: "/cms/advanced/catalogue",
          label: cmsNew("cms.new.auto.label.catalogue", "Catalogue"),
          icon: Route,
        },
        {
          path: "/cms/advanced/recherche",
          label: cmsNew("cms.new.auto.label.recherche", "Recherche"),
          icon: Search,
        },
        {
          path: "/cms/advanced/parcours-membre",
          label: cmsNew("cms.new.auto.label.membre", "Membre"),
          icon: Users,
        },
        {
          path: "/cms/advanced/abonnement",
          label: cmsNew("cms.new.auto.label.abonnement", "Abonnement"),
          icon: CreditCard,
        },
        {
          path: "/cms/advanced/authentification",
          label: cmsNew(
            "cms.new.nav.authenticationForm",
            "Formulaire d'authentification",
          ),
          icon: ShieldCheck,
        },
        {
          path: "/cms/advanced/e-shop",
          label: cmsNew("cms.new.nav.eshop", "E-shop"),
          icon: Store,
          requiresEshop: true,
        },
      ],
    },
    {
      title: cmsNew("cms.new.auto.title.site", "Site"),
      items: [
        {
          path: "/cms/advanced/maintenance",
          label: cmsNew("cms.new.auto.label.maintenance", "Maintenance"),
          icon: Wrench,
        },
      ],
    },
    {
      title: cmsNew(
        "cms.new.auto.title.structure_du_site",
        "Structure du site",
      ),
      items: [
        {
          path: "/cms/advanced/menus",
          label: cmsNew("cms.new.auto.label.menus", "Menus"),
          icon: ListTree,
        },
        {
          path: "/cms/advanced/en-tete",
          label: cmsNew("cms.new.auto.label.en_tete", "En-tête"),
          icon: LayoutTemplate,
        },
        {
          path: "/cms/advanced/pied-de-page",
          label: cmsNew("cms.new.auto.label.pied_de_page", "Pied de page"),
          icon: LayoutTemplate,
        },
      ],
    },
    {
      title: cmsNew("cms.new.categories.content", "Contenu"),
      items: [
        {
          path: "/cms/advanced/medias",
          label: cmsNew("cms.new.auto.label.medias", "Médias"),
          icon: ImageIcon,
        },
        {
          path: "/cms/advanced/decoupages",
          label: cmsNew("cms.new.auto.label.decoupages", "Découpages"),
          icon: Scissors,
        },
        {
          path: "/cms/advanced/news",
          label: cmsNew("cms.new.blocks.news.label", "Actualités"),
          icon: Newspaper,
        },
      ],
    },
    {
      title: cmsNew("cms.new.auto.title.configuration", "Configuration"),
      items: [
        {
          path: "/cms/advanced/apparence",
          label: cmsNew("cms.new.auto.label.apparence", "Apparence"),
          icon: Palette,
        },
        {
          path: "/cms/advanced/reglages",
          label: cmsNew("cms.new.auto.label.reglages", "Réglages"),
          icon: Settings,
        },
      ],
    },
  ];
}

export function CmsAdminShell({
  title,
  subtitle,
  actions,
  formIds,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Actions de la barre de titre (Enregistrer, Nouvelle page…). */
  actions?: ReactNode;
  /** Ids des <form> à surveiller (dirty tracking + garde de navigation). */
  formIds?: string[];
  children: ReactNode;
}) {
  const cmsNew = useCmsNew();
  const navGroups = useMemo(() => buildNavGroups(cmsNew), [cmsNew]);
  const location = useLocation();
  const navigate = useNavigate();
  const { instance, instanceData } = useAuth();
  const { t } = useTranslation();
  const frontUrl = (instanceData?.frontUrl ?? "").replace(/\/$/, "");
  const isAdvancedOnly = instanceData?.CMS?.cmsAccessMode === "ADVANCED";

  const handleSeeSite = () => {
    void openPublicSiteWithBypass(instanceData?.frontUrl ?? "");
  };

  // Aperçu : site entier sous `/advanced-preview` avec les BROUILLONS (en-tête,
  // pied de page, pages, menus). URL partageable ~3 h ; rien n'est publié.
  const handlePreviewSite = () => {
    void openPublicSiteInDraftPreview(instanceData?.frontUrl ?? "");
  };

  // Polices Google du thème (sélecteurs de typographie de l'Apparence).
  useEffect(() => {
    void loadCmsGoogleFonts();
  }, []);

  // Dirty tracking des formulaires surveillés (mécanique CMSLayout). Dépendance
  // SÉRIALISÉE : les appelants passent un littéral de tableau → sans ça l'effet
  // se ré-exécuterait à chaque render.
  const formIdsKey = (formIds ?? []).join(",");
  const dirtyFormsRef = React.useRef<Set<string>>(new Set());
  const [isDirty, setIsDirty] = React.useState(false);
  useEffect(() => {
    const ids = formIdsKey ? formIdsKey.split(",") : [];
    if (ids.length === 0) return;
    const cleanups: Array<() => void> = [];
    const markDirty = (id: string) => {
      if (!dirtyFormsRef.current.has(id)) {
        dirtyFormsRef.current.add(id);
        setIsDirty(true);
      }
    };
    const markClean = (id: string) => {
      dirtyFormsRef.current.delete(id);
      setIsDirty(dirtyFormsRef.current.size > 0);
    };
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!(el instanceof HTMLFormElement)) return;
      const onInput = () => markDirty(id);
      const onReset = () => markClean(id);
      const onSubmit = () => markClean(id);
      el.addEventListener("input", onInput);
      el.addEventListener("reset", onReset);
      el.addEventListener("submit", onSubmit);
      cleanups.push(() => {
        el.removeEventListener("input", onInput);
        el.removeEventListener("reset", onReset);
        el.removeEventListener("submit", onSubmit);
      });
    });
    return () => cleanups.forEach((fn) => fn());
  }, [formIdsKey]);

  const isActive = (item: NavItem) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path);

  return (
    <div className="flex min-h-screen bg-[#f0f0f1]">
      {/* ----- Barre latérale sombre (façon WP admin) — toujours sombre, même si
          le navigateur / l'OS est en mode clair (cf. reset global `button` dans
          index.css). ----- */}
      <aside className="cms-advanced-sidebar flex w-60 shrink-0 flex-col bg-[#1d2327] text-slate-300 [color-scheme:dark]">
        <div className="border-b border-white/10 px-4 py-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-white">
            {cmsNew("cms.new.shell.sidebarTitle", "CMS")}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            {instance ?? cmsNew("cms.new.auto.text.instance", "Instance")}
          </p>
          {frontUrl ? (
            <div className="mt-2 flex flex-col items-start gap-1">
              <button
                type="button"
                onClick={handleSeeSite}
                className="inline-flex appearance-none items-center gap-1.5 border-0 bg-transparent p-0 text-xs text-slate-400 transition-colors hover:text-white"
              >
                <ExternalLink size={12} />
                {cmsNew("cms.new.auto.text.voir_le_site", "Voir le site")}
              </button>
              <button
                type="button"
                onClick={handlePreviewSite}
                title={cmsNew(
                  "cms.new.shell.previewDraftSiteHint",
                  "Ouvre /advanced-preview avec les brouillons (navigation site entier). URL partageable ~3 h — rien n'est publié.",
                )}
                className="inline-flex appearance-none items-center gap-1.5 border-0 bg-transparent p-0 text-xs text-slate-400 transition-colors hover:text-white"
              >
                <Eye size={12} />
                {cmsNew(
                  "cms.new.shell.previewDraftSite",
                  "Aperçu du site (brouillon)",
                )}
              </button>
            </div>
          ) : null}
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto py-3">
          {navGroups.map((group) => (
            <div key={group.title} className="px-2">
              <p className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 first:pt-0">
                {group.title}
              </p>
              <div className="flex flex-col gap-0.5">
                {group.items
                  .filter(
                    (item) => !item.requiresEshop || instanceData?.haveEshop,
                  )
                  .map((item) => {
                    const active = isActive(item);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        type="button"
                        data-nav-active={active ? "true" : undefined}
                        onClick={() => navigate(item.path)}
                        className={cn(
                          "flex appearance-none items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2 text-left text-sm font-medium shadow-none transition-colors",
                          active
                            ? "bg-[#2271b1] text-white hover:bg-[#2271b1] hover:text-white"
                            : "text-slate-300 hover:bg-white/10 hover:text-white",
                        )}
                      >
                        <Icon size={16} className="shrink-0" />
                        {item.label}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </nav>

        {!isAdvancedOnly ? (
          <div className="border-t border-white/10 py-2">
            <button
              type="button"
              onClick={() => navigate("/cms")}
              className="flex w-full appearance-none items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left text-sm text-slate-400 shadow-none transition-colors hover:bg-white/10 hover:text-white"
            >
              <ArrowLeft size={16} className="shrink-0" />
              {cmsNew("cms.new.auto.text.ancien_cms", "Ancien CMS")}
            </button>
          </div>
        ) : null}
      </aside>

      {/* ----- Zone de contenu ----- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-6 py-3">
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-slate-900">{title}</h1>
            {subtitle ? (
              <p className="max-w-prose  text-xs text-slate-500">{subtitle}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="ml-auto flex items-center gap-2">{actions}</div>
          ) : null}
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>

      <ConfirmNavigation
        isBlocked={isDirty}
        title={t("cms.confirmNavigation.title")}
      />
    </div>
  );
}
